import type {Component} from 'panel';

import type {Store, SubscriptionCallback} from 'common/report/stores/store';

import {getNow, PERFORMANCE_EVENT_PREFIX} from './timing';
import {emitInfraTrackEvent, getCustomElementParents} from './dom';

export function useEventListener(
  component: Component<any, any, any, any, any>,
  target: EventTarget,
  event: string,
  listener: EventListenerOrEventListenerObject,
  listenerOptions?: AddEventListenerOptions,
) {
  target.addEventListener(event, listener, listenerOptions);
  component.onDisconnected(() => target.removeEventListener(event, listener, listenerOptions));
}

/**
 * Provides component integration for a particular store by adding
 * a subscriber that forces an update on the component by default
 * or invokes subscriptionCallback if passed; this subscription
 * is removed when the component is unmounted
 *
 * @param component
 * @param store
 * @param subscriptionCallback
 */
export function useStore<StoreValueT, StoreValueTRaw>(
  // TODO: this should be of the type `Component<unknown>`, but app components that use JSURLMixin mangles the type, needs to be fixed
  component: TODO,
  store: Store<StoreValueT, StoreValueTRaw>,
  subscriptionCallback?: SubscriptionCallback,
): Store<StoreValueT, StoreValueTRaw> {
  if (!subscriptionCallback) {
    subscriptionCallback = component.update.bind(component);
  }
  store.subscribe(subscriptionCallback);
  component.onDisconnected(() => store.unsubscribe(subscriptionCallback));
  return store;
}

export function useStoreAndFetchOnce<StoreValueT, StoreValueTRaw>(
  // TODO: this should be of the type `Component<unknown>`, but app components that use JSURLMixin mangles the type, needs to be fixed
  component: TODO,
  store: Store<StoreValueT, StoreValueTRaw>,
  subscriptionCallback?: SubscriptionCallback,
  fetchParams: {[key: string]: any} = {},
): Store<StoreValueT, StoreValueTRaw> {
  useStore<StoreValueT, StoreValueTRaw>(component, store, subscriptionCallback);
  if (!store.hasFetchedValue()) {
    store.fetch(fetchParams); // async, no need to wait
  }
  return store;
}

function getComponentTrackingProps(component: Component<unknown>, parents: string[] = [], props = {}) {
  const componentName = component.toString();

  return {
    sourceComponent: componentName.includes(`#`)
      ? componentName.substring(0, componentName.indexOf(`#`))
      : componentName,
    sourceComponentId: componentName.includes(`#`)
      ? componentName.substring(componentName.indexOf(`#`) + 1)
      : undefined,
    now: getNow(),
    parentPath: parents.join(`>`),
    parents,
    ...props,
    ...component.timings,
  };
}

export enum ComponentLifetimeEndEventName {
  LoadingDisplayed = `Loading Displayed`,
}

export enum ComponentLifetimeStartEventName {
  ComponentAdded = `Component Added`,
  LoadingAdded = `Loading Added`,
}

/**
 * This function will emit a track event for the total time taken to initialize the passed in component.
 * It is expected to be called AFTER super.connectedCallback to ensure all the internal timing values are set.
 */
export function trackInitialization(
  component: Component<any, any, any, any, any>,
  {
    eventName = ComponentLifetimeStartEventName.ComponentAdded,
    props = {},
  }: {
    eventName?: ComponentLifetimeStartEventName;
    props?: Record<string, any>;
  } = {},
) {
  component.onConnected(() => {
    const parents = getCustomElementParents(component);
    emitInfraTrackEvent(window, {
      eventName,
      eventPrefix: PERFORMANCE_EVENT_PREFIX,
      props: {
        elapsedMs: (component.timings.initializingCompletedAt ?? getNow()) - component.timings.initializingStartedAt,
        ...getComponentTrackingProps(component, parents as string[], props),
      },
    });
  });
}
